import { useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { Email } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useGetDefaultFrom } from '@/hooks/useGetDefaultFrom';
import { useEmailComposer } from '@/components/shared/email/useEmailComposer';
import {
  MAX_ATTACHMENT_FILES,
  KULA_PER_FILE_PASSTHROUGH_MB,
  ATTACHMENT_TOOLTIP,
  TOTAL_SIZE_TOAST,
  exceedsTotalAttachmentSize,
} from '@/components/shared/email/attachmentLimits';
import { useShowToast } from '@/hooks/useShowToast';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import {
  isTemplateEdited,
  pickDefaultTemplateOption,
} from '@/components/shared/email/templateSelection';
import {
  applyTemplateMetadataOnChange,
  compositionFromTemplateOption,
} from '@/components/shared/interview-manager/InterviewActivity/communication/helpers/templateApply';
import {
  SurveyNode,
  SurveyLinkReplacement,
} from '@/components/formBuilder/formFields/FormEditor/extensions';
import { EditorSurvey } from '@/components/formBuilder/formFields/FormEditor/survey/EditorSurvey';
import {
  toEmailValue,
  fromEmailValue,
  toEditorAttachments,
  attachmentKey,
} from './rejectEmailValue';
import type { RejectEmailConfig, RejectAttachment } from './rejectEmailValue';

interface SurveySelection {
  value?: string | number;
  label?: string;
}

interface RejectTemplate {
  id: string | number;
  name?: string;
  body?: string;
  subject?: string;
  attachments?: unknown;
  // kula's `SelectOption.data` is an open record; templates carry more fields
  // than we name here and the send payload still reads them.
  [key: string]: unknown;
}

interface RejectEmailComposerProps {
  config: RejectEmailConfig;
  candidateEmail?: string;
  templates?: RejectTemplate[];
  onChange: (_config: RejectEmailConfig) => void;
  onTemplateChange?: (_template: RejectTemplate | null) => void;
  attachments?: RejectAttachment[];
  onAttachmentsChange?: (_items: RejectAttachment[]) => void;
  selectedSurvey?: SurveySelection | null;
  onSurveySelect?: (_survey: SurveySelection) => void;
  onSurveyRemove?: () => void;
  submitAttempt?: number;
}

// The survey pill from the pre-kula select (same look as the fraud detector's).
// kula `Tag` has no pastel-5 survey surface, hence Chakra + theme tokens.
const SurveyBadge = () => (
  <Flex bg="pastel-5-light" borderRadius="4px" p="4px 8px">
    <KText lineHeight="16px" fontSize="13px" color="pastel-5-dark">
      Has survey
    </KText>
  </Flex>
);

// Rejection emails ship as real HTML, so offer the template editor's typography.
const TOOLBAR_FEATURES = {
  image: true,
  attachment: true,
  fontSize: true,
  fontFamily: true,
} as const;

/**
 * Reject-email composition on kula-ai `Email`, which owns the template picker.
 *
 * The legacy `FormEditor` disabled tiptap's `hardBreak`, so it dropped every
 * `<br>` in a stored template body (ENG-3719).
 */
export const RejectEmailComposer = ({
  config,
  candidateEmail,
  templates = [],
  onChange,
  onTemplateChange,
  attachments = [],
  onAttachmentsChange,
  selectedSurvey = null,
  onSurveySelect,
  onSurveyRemove,
  submitAttempt = 0,
}: RejectEmailComposerProps) => {
  const { fromUsers, allUsers } = useGetAllUsers();
  // LD override → default shared mailbox → signed-in user; don't re-implement.
  const { from: defaultFrom } = useGetDefaultFrom({});

  // `rightElement` keeps the survey pill inline with the template name; as a
  // `description` it stacked onto a second line and bolded the label (CORE-5175).
  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name ?? '',
        value: String(template.id),
        rightElement: String(template.body ?? '').includes(
          '{{survey.link}}'
        ) ? (
          <SurveyBadge />
        ) : undefined,
        data: template,
      })),
    [templates]
  );

  // Stored sender wins; adopt one only if the select can actually render it.
  const storedOrDefaultFrom = config?.from || defaultFrom;
  const wantedFrom = fromUsers.some(
    (user) => user.email === storedOrDefaultFrom
  )
    ? storedOrDefaultFrom
    : undefined;

  // This composer is prop-driven, not Formik-backed: the modal owns the config
  // and takes edits through `onChange`.
  const { value, valueRef, handleChange, fromOptions, ccBccOptions } =
    useEmailComposer({
      formik: { values: config },
      toValue: () => toEmailValue(config, candidateEmail),
      toPatch: fromEmailValue,
      fromUsers,
      ccBccUsers: allUsers,
      valueKey: 'email',
      // Committed through the hook's write path, not derived for display: a
      // display-only prefill left the modal's Formik `from` empty, so the
      // mandatory-From check blocked the send it visibly satisfied (ENG-3817).
      wantedFrom,
      // kula applies the picked template's body; this merges its subject too.
      normalise: (prev, next) =>
        applyTemplateMetadataOnChange(prev, next, templateOptions),
      onTemplateChange: (templateId) => {
        const picked = templateOptions.find((o) => o.value === templateId);
        onTemplateChange?.(picked?.data ?? null);
      },
      applyPatch: (patch) => onChange(patch as RejectEmailConfig),
    });

  // Open on the default template in create mode. A saved config carries its own
  // copy, so only an untouched composition is seeded.
  const didSeedTemplate = useRef(false);
  useEffect(() => {
    if (didSeedTemplate.current || !templateOptions.length) return;
    didSeedTemplate.current = true;
    const current = valueRef.current;
    if (current.template || current.subject?.trim() || current.content?.trim())
      return;

    const option = pickDefaultTemplateOption(templateOptions);
    if (option) handleChange(compositionFromTemplateOption(current, option));
  }, [templateOptions, value, handleChange, valueRef]);

  const selectedOption = useMemo(
    () => templateOptions.find((option) => option.value === value.template),
    [templateOptions, value.template]
  );

  // Memo on the flag, not on `value` — a new object per keystroke would give
  // kula's template `Select` a fresh identity on every edit.
  const templateEdited = useMemo(
    () => isTemplateEdited(value, selectedOption),
    [value, selectedOption]
  );

  const templateSelectProps = useMemo(
    () => customTemplateSelectProps(templateEdited),
    [templateEdited]
  );

  // Formik holds a mixed File / template-row array; map to chips and back by key.
  const editorAttachments = useMemo(
    () => toEditorAttachments(attachments),
    [attachments]
  );

  const toast = useShowToast();

  // Any number of files, capped by total size — kula's own count/size caps are
  // raised out of the way so its tooltip doesn't read "Attach file (2/5)".
  const attachmentCaps = {
    attachmentMaxFiles: MAX_ATTACHMENT_FILES,
    attachmentMaxFileSizeMb: KULA_PER_FILE_PASSTHROUGH_MB,
    attachmentTooltip: ATTACHMENT_TOOLTIP,
  };

  const attachmentHandlers = useMemo(
    () =>
      onAttachmentsChange
        ? {
            attachments: editorAttachments,
            onAttachFiles: (files: File[]) => {
              if (exceedsTotalAttachmentSize(editorAttachments, files)) {
                toast(TOTAL_SIZE_TOAST);
                return;
              }
              onAttachmentsChange([...attachments, ...files]);
            },
            onRemoveAttachment: (key: string) =>
              onAttachmentsChange(
                attachments.filter((item, i) => attachmentKey(item, i) !== key)
              ),
          }
        : { attachments: editorAttachments },
    [attachments, editorAttachments, onAttachmentsChange, toast]
  );

  // kula hands over the tiptap instance once the editor mounts.
  const [editorInstance, setEditorInstance] = useState<unknown>(null);

  // Read through a ref: a new extension array remounts the editor and resets the caret.
  const surveyHandlers = useRef({ onSurveySelect, onSurveyRemove });
  surveyHandlers.current = { onSurveySelect, onSurveyRemove };

  // Read through a ref for the same reason as the handlers above. This used to
  // depend on `selectedSurvey?.value`, so picking a survey rebuilt the array and
  // remounted the editor, which reloaded the body and reset the pill to
  // "Select a survey" — and the send then failed its survey check (ENG-3826).
  const surveyIdRef = useRef<SurveySelection['value'] | null>(
    selectedSurvey?.value ?? null
  );
  surveyIdRef.current = selectedSurvey?.value ?? null;

  // Repo-local tiptap nodes; kula peer-deps tiptap so they share one instance.
  const extraExtensions = useMemo(
    () => [
      SurveyNode.configure({
        onSurveySelect: (survey: SurveySelection) =>
          surveyHandlers.current.onSurveySelect?.(survey),
        onSurveyRemove: () => surveyHandlers.current.onSurveyRemove?.(),
      }),
      SurveyLinkReplacement.configure({
        initialSurveyId: () => surveyIdRef.current,
      }),
    ],
    []
  );

  return (
    <Email
      showFrom
      showTo={false}
      showSubject
      showTemplate
      showEditor
      enableCcBcc
      applyTemplateBodyToEditor
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      ccBccOptions={ccBccOptions}
      templateOptions={templateOptions}
      templateSelectProps={templateSelectProps}
      submitAttempt={submitAttempt}
      placeholders={{
        from: 'Select from',
        subject: 'Enter subject',
        template: 'Select email template',
      }}
      editorProps={{
        // The extensions only render an existing pill; this inserts a new one.
        toolbarStartSlot: editorInstance ? (
          <EditorSurvey
            editor={editorInstance}
            color={undefined}
            isActive={(editorInstance as any)?.isActive?.('surveySelect')}
            onSurveySelect={onSurveySelect}
            onSurveyRemove={onSurveyRemove}
          />
        ) : null,
        onEditorReady: setEditorInstance,
        placeholder: 'Start typing...',
        // Pasted/inserted images POST to `/user_image_uploads` and the editor keeps the
        // returned URL; without it the body stores a base64 data URL.
        uploadInlineImage: uploadUserInlineImage,
        toolbarFeatures: TOOLBAR_FEATURES,
        extraExtensions,
        ...attachmentCaps,
        ...attachmentHandlers,
      }}
    />
  );
};
